<template>
  <div class="slot-full-schedule">
    <template v-if="isLink">
      <sp-icon class="slot-full-schedule__icon" :name="icon" size="large"></sp-icon>
      <sp-button
        class="slot-full-schedule__link"
        :data-testid="TestId.slots.fullSchedule"
        :data-analytics-element="analyticsElement"
        data-analytics-interaction="click"
        variant="appearance-link"
        @click="open"
      >
        <slot></slot>
      </sp-button>
    </template>

    <sp-button
      v-else
      class="slot-full-schedule__button"
      :data-testid="TestId.slots.fullSchedule"
      :data-analytics-element="analyticsElement"
      data-analytics-interaction="click"
      color="primary"
      :variant="buttonVariant"
      @click="open"
    >
      <sp-icon slot="prepend" :name="icon" :size="iconSize"></sp-icon>
      <slot></slot>
    </sp-button>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import { trackControlClick } from "#ce/components/SpFinder/lib/click-analytics";

const props = defineProps({
  /**
   * "link" sets a large icon beside a link; "button" puts the icon inside a button.
   *
   * @type {String}
   */
  variant: {
    type: String,
    default: "link",
    validator: (value) => ["link", "button"].includes(value),
  },
  /**
   * `sp-button` variant of the "button" form.
   *
   * @type {String}
   */
  buttonVariant: {
    type: String,
    default: "text",
  },
  /**
   * `sp-icon` name.
   *
   * @type {String}
   */
  icon: {
    type: String,
    required: true,
  },
  /**
   * `data-analytics-element` value.
   *
   * @type {String}
   */
  analyticsElement: {
    type: String,
    required: true,
  },
  /**
   * `sp-icon` size. `null` leaves the size to the `--sp-comp-button-*-icon-size` token.
   *
   * @type {String}
   */
  iconSize: {
    type: String,
    default: "small",
  },
});

const emit = defineEmits(["open"]);

const isLink = computed(() => props.variant === "link");

function open(event) {
  trackControlClick(event);
  emit("open");
}
</script>

<style scoped lang="scss">
.slot-full-schedule {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-ref-scale-8);
}

.slot-full-schedule__icon {
  display: var(--sp-comp-slot-full-schedule-button-icon-display, block);
}

.slot-full-schedule__link {
  text-align: var(--sp-comp-slot-full-schedule-button-link-text-align, start);
  --sp-comp-button-font-weight: var(--sp-comp-slot-full-schedule-button-link-font-weight);
}

.slot-full-schedule__button {
  width: auto;
  box-shadow: var(--sp-comp-slot-full-schedule-button-box-shadow, none);
  --sp-comp-button-font-weight: var(--sp-comp-slot-full-schedule-button-font-weight);

  sp-icon {
    margin-inline-end: var(--sp-comp-slot-full-schedule-button-icon-gap, 0);
  }
}
</style>
